import type { FilterSchema } from '@/shared/components/filter/validation'
import type { ChartConfig } from '@/shared/components/ui/chart'
import type {
	BIImpactRequest,
	BIImpactResponse,
	FilterRuleModel,
} from '@/shared/generated'
import type { RenderableText } from 'recharts'
import type { WidgetPeriod, WidgetPeriods } from '../types'

import { useMemo, useState } from 'react'
import { useWidgetParams } from '@/pages/dashboard/lib/useWidgetParams'
import { useFlag } from '@openfeature/react-sdk'
import { endOfWeek, format, isAfter, startOfWeek, subDays } from 'date-fns'
import { map, pipe } from 'lodash/fp'
import { useTranslation } from 'react-i18next'
import { Bar, BarChart, CartesianGrid, LabelList, XAxis, YAxis } from 'recharts'

import { useBiImpactQuery, zipSeries } from '@/entities/bi'
import { useSelectedCurrency } from '@/entities/currency'

import { Operators } from '@/shared/components/query-builder'
import { ChartContainer } from '@/shared/components/ui/chart'
import {
	Select,
	SelectContent,
	SelectGroup,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from '@/shared/components/ui/select'
import { PALETTE } from '@/shared/lib/theme-palette'
import { cn } from '@/shared/lib/utils'
import { DateFormat } from '@/shared/tools/dates'
import { formatMoney, NumberFormats } from '@/shared/tools/locale'

import { MainDashboardFilter } from '../../Filter'
import { SinglePeriodSelect } from '../../Periods'
import { WidgetContent, WidgetHeader } from '../../Widget'
import Widget from '../../Widget/Widget'
import WidgetEmptyState from '../../WidgetEmptyState'
import WidgetErrorStateInside from '../../WidgetErrorStateInside'
import WidgetLoadingState from '../../WidgetLoadingState'

/**
 * Recharts (shadcn charts) port of {@link ./Widget} (Highcharts `column`) for
 * the charting-library migration spike (PCT-976): a single green column
 * series over weekly categories with compact data labels above the bars, no
 * tooltip and no legend. The Highcharts `scrollablePlotArea` maps to a
 * `minWidth` on the chart container inside the `overflow-x-auto` wrapper, and
 * `minPointLength: 5` maps to `minPointSize={5}` on the `Bar`. The Highcharts
 * `xAxis.minRange: 10` (a fixed minimum of 11 tick positions, so sparse data
 * stays left-aligned with blank trailing ticks) maps to padding the rows with
 * empty-name / `null`-value entries, which render ticks but no bars. Kept
 * alongside the Highcharts version so both can be compared.
 */

const METRICS = ['revenue', 'gross_profit', 'revenue_boost', 'profit_boost']
const BI_IMPACT_FILTER: FilterRuleModel = {
	name: 'acceptance_status',
	operation: Operators.IN,
	value: ['accepted', 'changed_manually'],
}
type Metric = 'revenue' | 'gross_profit' | 'revenue_boost' | 'profit_boost'
type BIImpactDataType = {
	week_num: number
	[key: string]: number
}
type ChartPoint = {
	name: string
	value: number | null
}
const maxDate = new Date()

// Highcharts `xAxis.minRange: 10` equivalent: the axis always spans at least
// 11 category slots (0..10), so sparse data is left-aligned.
const MIN_AXIS_CATEGORIES = 11
const padChartData = (data: ChartPoint[]): ChartPoint[] => [
	...data,
	...Array.from(
		{ length: Math.max(0, MIN_AXIS_CATEGORIES - data.length) },
		(): ChartPoint => ({ name: '', value: null })
	),
]

const alignEndDateToCompleteWeek = (date: string) => {
	if (isAfter(endOfWeek(new Date(date)), new Date())) {
		return subDays(startOfWeek(new Date(date), { weekStartsOn: 1 }), 1)
	}

	return new Date(date)
}
const alignStartDateToCompleteWeek = (date: string) =>
	startOfWeek(new Date(date), { weekStartsOn: 1 })

const alignPeriods = (period: WidgetPeriod) => ({
	start_date: format(
		alignStartDateToCompleteWeek(period.start_date),
		DateFormat.system
	),
	end_date: format(
		alignEndDateToCompleteWeek(period.end_date),
		DateFormat.system
	),
})
export default function BusinessMetricBoostsWidget({
	commonFilters = [],
	commonPeriods,
	className,
}: {
	commonFilters: FilterSchema[]
	commonPeriods: WidgetPeriods
	className?: string
}) {
	const { t } = useTranslation()

	const boostMetricsFlag = useFlag('boost_metrics', true, {
		suspendUntilReady: true,
	})
	const metrics = useMemo(
		() => (boostMetricsFlag.value ? METRICS : ['revenue', 'gross_profit']),
		[boostMetricsFlag.value]
	)
	const [metric, setMetric] = useState<Metric>('revenue')
	const [period, setPeriod] = useState<WidgetPeriod>(() => commonPeriods.main)
	const { params, onChangeParams } = useWidgetParams()
	const filters = [...commonFilters, ...(params?.filters ?? [])]
	const currency = useSelectedCurrency()
	const {
		data: biImpact,
		isPending,
		isError,
	} = useBiImpactQuery<ChartPoint[]>(
		{
			currency,
			label: 'BusinessMetricBoosts',
			metrics,
			dimensions: ['week_num'],
			filters: [BI_IMPACT_FILTER, ...filters],
			...alignPeriods(period),
		} as BIImpactRequest,
		{
			enabled: !!period,
			select: (data: BIImpactResponse | null) =>
				pipe(
					(data) => zipSeries(data?.meta, data?.data),
					map(({ week_num, [metric]: data }: BIImpactDataType) => {
						const yearShort = String(week_num)?.slice(2, 4)
						const week = String(Number(String(week_num).slice(4)))

						return {
							value: data > 0 ? data : null,
							name: `${t('general.week_short', { defaultValue: 'W' })}${week}'${yearShort}`,
						}
					})
				)(data),
		}
	)
	const chartData = useMemo(() => padChartData(biImpact ?? []), [biImpact])
	const chartConfig = {
		value: {
			label: t(`metric.${metric}`, { defaultValue: metric }),
			color: PALETTE.green.DEFAULT,
		},
	} satisfies ChartConfig

	return (
		<Widget
			className={cn(
				'relative flex flex-auto flex-col overflow-hidden',
				className
			)}
		>
			<WidgetHeader
				title={t('widget.business_metric_boosts.title', {
					defaultValue: 'Business Metric Boosts',
				})}
				tooltip={t('widget.business_metric_boosts.tooltip', {
					defaultValue: 'widget.business_metric_boosts.tooltip',
				})}
				subtitle={t('widget.business_metric_boosts.subtitle', {
					defaultValue: 'Monthly CYTD',
				})}
			>
				<div className='flex items-center gap-2'>
					<Select
						value={metric}
						onValueChange={(value: Metric) => setMetric(value)}
					>
						<SelectTrigger>
							<SelectValue />
						</SelectTrigger>
						<SelectContent>
							<SelectGroup>
								{metrics.map((metric) => (
									<SelectItem key={metric} value={metric}>
										{t(`metric.${metric}`, { defaultValue: metric })}
									</SelectItem>
								))}
							</SelectGroup>
						</SelectContent>
					</Select>
					<MainDashboardFilter
						irremovableCount={commonFilters.length}
						filters={filters ?? []}
						onChange={(filters) => {
							onChangeParams({ ...params, filters })
						}}
					/>
					{commonPeriods?.main && (
						<SinglePeriodSelect
							period={period || commonPeriods?.main}
							defaultPeriod={commonPeriods?.main || {}}
							onChangePeriod={(period) => {
								setPeriod(period)
							}}
							maxDate={maxDate}
						/>
					)}
				</div>
			</WidgetHeader>
			<WidgetLoadingState isTinted={isPending} />
			<WidgetContent>
				{!!biImpact?.length && !isPending && !isError && (
					<div className='w-full scroll-fade-x overflow-x-auto'>
						<div>
							<ChartContainer
								config={chartConfig}
								className='h-55 w-full'
								style={{ minWidth: chartData.length * 40 }}
							>
								<BarChart
									accessibilityLayer={false}
									data={chartData}
									margin={{ top: 16, left: 8, right: 8 }}
								>
									<CartesianGrid vertical={false} strokeDasharray='3 3' />
									<XAxis
										dataKey='name'
										axisLine={false}
										fontSize={10}
										interval={0}
									/>
									<YAxis hide yAxisId='0' domain={[0, 'auto']} />
									<Bar
										dataKey='value'
										fill='var(--color-value)'
										barSize={30}
										minPointSize={1}
									>
										<LabelList
											dataKey='value'
											position='top'
											offset={6}
											fontSize={8}
											fill='var(--foreground)'
											formatter={(value: RenderableText) =>
												typeof value !== 'number'
													? ''
													: formatMoney(value, currency, NumberFormats.Compact)
											}
										/>
									</Bar>
								</BarChart>
							</ChartContainer>
						</div>
					</div>
				)}

				{!isPending && isError && !biImpact?.length && (
					<WidgetErrorStateInside />
				)}
				{!isPending && !isError && !biImpact?.length && <WidgetEmptyState />}
			</WidgetContent>
		</Widget>
	)
}
